// Native (Capacitor) tenant + API base resolution.
//
// The web build is served from the tenant's own origin, so API/WS calls are
// relative ("" base). The packaged Android app has no server origin of its own,
// so on first launch the user enters their tenant host (e.g.
// "mo-sffd.cad.watchcenter365.com") and every request is pointed at it.
//
// The value is kept in localStorage (synchronous, and sandboxed to the app's
// private WebView on Android) so client.ts and the WebSocket hook can read it
// without an async bootstrap.

const TENANT_KEY = 'tenant_base';

export function isNative(): boolean {
  const cap = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  return !!cap?.isNativePlatform?.();
}

/** Normalize user input into an "https://host" origin (no trailing slash, no path). */
export function normalizeTenantBase(input: string): string {
  let v = input.trim();
  if (!v) return '';
  if (!/^https?:\/\//i.test(v)) v = `https://${v}`;
  try {
    const u = new URL(v);
    return `${u.protocol}//${u.host}`;
  } catch {
    return '';
  }
}

export function getTenantBase(): string {
  return localStorage.getItem(TENANT_KEY) || '';
}

export function setTenantBase(input: string): string {
  const base = normalizeTenantBase(input);
  if (base) localStorage.setItem(TENANT_KEY, base);
  return base;
}

export function clearTenantBase(): void {
  localStorage.removeItem(TENANT_KEY);
}

/** HTTP API base. "" (relative) on web; the tenant origin in the native app. */
export function getApiBase(): string {
  if (isNative()) return getTenantBase();
  return '';
}

/** WebSocket base ("wss://host"). Falls back to the current page origin on web. */
export function getWsBase(): string {
  if (isNative()) {
    const base = getTenantBase();
    if (base) return base.replace(/^http/i, 'ws');
    return '';
  }
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}`;
}
